import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import { Button } from "reactstrap";
import ToastError from "../NotificationToast/ToastError";
import ToastSuccess from "../NotificationToast/ToastSuccess";
import { ScaleLoader } from "react-spinners";
import "../../assets/css/style.css";
import { APILA } from "../../config/apila";
import { APIUS } from "../../config/apius";
import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function Login() {
  const navigate = useNavigate();
  document.title = "Login Student";

  const [loading, setLoading] = useState(false);

  const [showPasscode, setShowPasscode] = useState(false);


  const [form, setForm] = useState({
    nisn: "",
    ysb_passcode_student: "",
  });

  // input yang diketik user
  const [usernameQuery, setUsernameQuery] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);

  // dropdown suggestions
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestError, setSuggestError] = useState("");

  // cache list (fallback kalau API tidak support query param)
  const [allStudentsCache, setAllStudentsCache] = useState(null);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const { nisn, ysb_passcode_student } = form;

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const buildStudentLabel = (item) => {
    const nama =
      item?.nama_lengkap ||
      item?.nama_siswa ||
      item?.nama ||
      item?.full_name ||
      "-";

    // kelas biasanya ada di key: kelas atau nama_kelas
    const kelasVal = item?.kelas || item?.nama_kelas || item?.kelas_name || "";

    // grade level bisa beda-beda key (kalau ada)
    const gradeVal =
      item?.ysb_grade_level_name ||
      item?.grade_level_name ||
      item?.nama_jenjang ||
      item?.jenjang ||
      item?.tingkat ||
      "";

    const sekolah = item?.ysb_school_id || item?.sekolah || item?.school_id || "";

    const nisn_ = item?.nisn

    // contoh output: "Hafy Raski Prima - SD 1 A - SDIA44"
    const gradePlusKelas = `${gradeVal ? `${gradeVal} ` : ""}${kelasVal}`.trim();
    const mid = gradePlusKelas ? ` - ${gradePlusKelas}` : "";
    const tail = sekolah ? ` - ${sekolah}` : "";
    const nisn = nisn_ ? ` - ${nisn_}` : "";
    // return `${nama}${mid}${tail}${nisn}`;
    // return `${nama}${nisn}`;
    return `${nama}${tail}`;
  };

  const normalize = (s) => String(s || "").toLowerCase().trim();

  const strictNameMatch = (fullName, query) => {
    const q = normalize(query);
    if (!q) return true;

    const tokens = q.split(/\s+/).filter(Boolean); // kata yang diketik user
    const words = normalize(fullName).split(/\s+/).filter(Boolean); // kata di nama siswa

    // setiap token HARUS cocok dengan AWAL salah satu kata di nama siswa
    return tokens.every((t) => words.some((w) => w.startsWith(t)));
  };

  // Ambil data siswa dari API (dengan search param).
  // Kalau ternyata API tidak support param dan data kosong, fallback:
  // - load list tanpa param 1x (cache)
  // - filter lokal
  const fetchStudents = async (keyword) => {
    // const url =
    //   "https://api.us.sims.alazharsummarecon.sch.id/api/access/students-angket-nisn";

    

    // 1) request dengan param search
    const tryRemote = async () => {
      const resp = await APIUS.get(
        "/api/access/students-angket-nisn",
        {
        params: { search: keyword }, // <-- kalau backend pakai param lain, ganti di sini
        headers: {
          Accept: "application/json",
        },
      });
      const list = resp?.data?.data;
      return Array.isArray(list) ? list : [];
    };

    // 2) fallback: request tanpa param (untuk cache)
    const loadAll = async () => {
      const resp = await APIUS.get(
        "/api/access/students-angket-nisn",
        {
        headers: {
          Accept: "application/json",
        },
      });
      const list = resp?.data?.data;
      return Array.isArray(list) ? list : [];
    };

    let remoteList = [];
    try {
      remoteList = await tryRemote();
    } catch (e) {
      // kalau error remote, kita tetap coba fallback cache
      remoteList = [];
    }

    // kalau remote list ada hasil, pakai itu
    if (remoteList.length > 0) return remoteList;

    // kalau belum punya cache, load dulu
    let all = allStudentsCache;
    if (!Array.isArray(all) || all.length === 0) {
      try {
        all = await loadAll();
        setAllStudentsCache(all);
      } catch (e) {
        // fallback gagal juga
        all = [];
      }
    }

    // filter lokal by nama_lengkap / nisn / sekolah / kelas
    const kw = normalize(keyword);
    const filtered = (all || []).filter((it) => {
      const nama = normalize(it?.nama_lengkap || it?.nama_siswa);
      const n = normalize(it?.nisn);
      const sekolah = normalize(it?.ysb_school_id);
      const kelas = normalize(it?.kelas || it?.nama_kelas);
      const grade = normalize(it?.ysb_grade_level_name || it?.grade_level_name);
      return (
        nama.includes(kw) ||
        n.includes(kw) ||
        sekolah.includes(kw) ||
        kelas.includes(kw) ||
        grade.includes(kw)
      );
    });

    return filtered.slice(0, 20);
  };

  // close dropdown ketika klik di luar
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setIsSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // debounce search ketika user mengetik username
  useEffect(() => {
    // kalau user sudah memilih siswa, dan user belum mengubah input, tidak usah cari lagi
    if (selectedStudent && usernameQuery === buildStudentLabel(selectedStudent)) {
      return;
    }

    // jika user mulai mengetik ulang, anggap belum memilih
    if (selectedStudent) {
      setSelectedStudent(null);
      setForm((prev) => ({ ...prev, nisn: "" }));
    }

    const q = usernameQuery.trim();

    setSuggestError("");

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < 2) {
      setSuggestions([]);
      setIsSuggestOpen(false);
      setSuggestLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      setIsSuggestOpen(true);
      try {
        // const list = await fetchStudents(q);
        // setSuggestions(list);
        // if (list.length === 0) {
        //   setSuggestError("Data tidak ditemukan.");
        // }

        const list = await fetchStudents(q);

        // FILTER KETAT: hasil wajib cocok dengan input user
        const filtered = (list || []).filter((it) =>
          strictNameMatch(it?.nama_lengkap || it?.nama_siswa || "", q)
        );

        setSuggestions(filtered);

        if (filtered.length === 0) {
          setSuggestError("Data tidak ditemukan.");
        }
      } catch (err) {
        setSuggestions([]);
        setSuggestError(
          err?.response?.data?.message || err?.message || "Gagal memuat data siswa."
        );
      } finally {
        setSuggestLoading(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameQuery]);

  const onPickStudent = (item) => {
    setSelectedStudent(item);
    setUsernameQuery(buildStudentLabel(item));
    setForm((prev) => ({
      ...prev,
      nisn: String(item?.nisn || "").trim(), // payload login tetap NISN
    }));
    setIsSuggestOpen(false);
    setSuggestions([]);
    setSuggestError("");
  };

  // const handleSubmit = useMutation(async (e) => {
  //   try {
  //     e.preventDefault();

  //     // validasi: pastikan NISN terisi (dari pilihan autocomplete atau ketik manual)
  //     const nisnFinal = String(form.nisn || "").trim();
  //     const passFinal = String(form.ysb_passcode_student || "").trim();

  //     if (!nisnFinal) {
  //       ToastError.fire({
  //         icon: "error",
  //         title: "Silakan pilih siswa terlebih dahulu dari daftar yang muncul.",
  //       });
  //       return;
  //     }

  //     setLoading(true);

  //     // const url = "https://api.la.sims.alazharsummarecon.sch.id/api/auth-login/student";

      

  //     const payload = {
  //       nisn: nisnFinal,
  //       ysb_passcode_student: passFinal,
  //     };

  //     const response = await APILA.post("/api/auth-login/student", payload, {
  //       headers: {
  //         "Content-Type": "application/json",
  //         Accept: "application/json",
  //       },
  //     });

  //     const resData = response?.data?.data || {};
  //     const now = Date.now();
  //     const expiryTime = now + 24 * 60 * 60 * 1000;

  //     localStorage.setItem("student_logged_in", "true");
  //     localStorage.setItem("token_expiry", String(expiryTime));
  //     localStorage.setItem("student_profile", JSON.stringify(resData));
  //     localStorage.setItem("ysb_student_id", resData?.ysb_student_id ?? "");
  //     localStorage.setItem("nama_siswa", resData?.nama_siswa ?? "");
  //     localStorage.setItem("ysb_semester_id", resData?.ysb_semester_id ?? "");
  //     localStorage.setItem("ysb_branch_id", resData?.ysb_branch_id ?? "");
  //     localStorage.setItem("ysb_school_id", resData?.ysb_school_id ?? "");
  //     localStorage.setItem("ysb_grade_level_id", resData?.ysb_grade_level_id ?? "");
  //     localStorage.setItem("ysb_kelas_id", resData?.ysb_kelas_id ?? "");
  //     localStorage.setItem("ysb_teacher_id", resData?.ysb_teacher_id ?? "");
  //     localStorage.setItem("ysb_teacher_id2", resData?.ysb_teacher_id2 ?? "");
  //     localStorage.setItem("nisn", payload.nisn);

  //     if (response?.status === 200 && response?.data?.error === false) {
  //       setLoading(false);
  //       ToastSuccess.fire({
  //         icon: "success",
  //         title: response?.data?.message || "Login berhasil",
  //         background: "#1d3557",
  //         color: "#ffffff",
  //       });
  //       navigate("/dashboard-siswa");
  //     } else {
  //       throw new Error(response?.data?.message || "Login gagal");
  //     }
  //   } catch (error) {
  //     setLoading(false);
  //     ToastError.fire({
  //       icon: "error",
  //       title:
  //         error?.response?.data?.message ||
  //         error?.message ||
  //         "Login gagal, cek NISN/Passcode",
  //     });
  //   }
  // });

  const handleSubmit = useMutation(async (e) => {
  try {
    e.preventDefault();

    const nisnFinal = String(form.nisn || "").trim();
    const passFinal = String(form.ysb_passcode_student || "").trim();
    const studentIdFinal = String(selectedStudent?.id || "").trim();

    // validasi utama sekarang pakai ID student, bukan NISN
    if (!studentIdFinal) {
      ToastError.fire({
        icon: "error",
        title: "Silakan pilih siswa terlebih dahulu dari daftar yang muncul.",
      });
      return;
    }

    if (!passFinal) {
      ToastError.fire({
        icon: "error",
        title: "Silakan isi passcode terlebih dahulu.",
      });
      return;
    }

    setLoading(true);

    const payload = {
      nisn: nisnFinal || null,
      ysb_student_id: studentIdFinal,
      ysb_passcode_student: passFinal,
    };

    const response = await APILA.post("/api/auth-login/student", payload, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    const resData = response?.data?.data || {};
    const now = Date.now();
    const expiryTime = now + 24 * 60 * 60 * 1000;

    localStorage.setItem("student_logged_in", "true");
    localStorage.setItem("token_expiry", String(expiryTime));
    localStorage.setItem("student_profile", JSON.stringify(resData));
    localStorage.setItem("ysb_student_id", resData?.ysb_student_id ?? "");
    localStorage.setItem("nama_siswa", resData?.nama_siswa ?? "");
    localStorage.setItem("ysb_semester_id", resData?.ysb_semester_id ?? "");
    localStorage.setItem("ysb_branch_id", resData?.ysb_branch_id ?? "");
    localStorage.setItem("ysb_school_id", resData?.ysb_school_id ?? "");
    localStorage.setItem("ysb_grade_level_id", resData?.ysb_grade_level_id ?? "");
    localStorage.setItem("ysb_kelas_id", resData?.ysb_kelas_id ?? "");
    localStorage.setItem("ysb_teacher_id", resData?.ysb_teacher_id ?? "");
    localStorage.setItem("ysb_teacher_id2", resData?.ysb_teacher_id2 ?? "");
    localStorage.setItem("nisn", payload.nisn ?? "");

    if (response?.status === 200 && response?.data?.error === false) {
      setLoading(false);
      ToastSuccess.fire({
        icon: "success",
        title: response?.data?.message || "Login berhasil",
        background: "#1d3557",
        color: "#ffffff",
      });
      navigate("/dashboard-siswa");
    } else {
      throw new Error(response?.data?.message || "Login gagal");
    }
  } catch (error) {
    setLoading(false);
    ToastError.fire({
      icon: "error",
      title:
        error?.response?.data?.message ||
        error?.message ||
        "Login gagal, cek Nama Siswa/Passcode",
    });
  }
});

  const suggestList = useMemo(() => {
    // tampilkan max 12 agar rapi
    return (suggestions || []).slice(0, 20);
  }, [suggestions]);

  return (
    <div className="login-page-v2">
      <div className="login-main-wrap">
        <div className="login-hero-icon">
          <span className="icon-hero-emoji">🎓</span>
        </div>

        <div className="login-single-card">
          <h3 className="login-card-title">Login Siswa</h3>
          <p className="login-card-subtitle"></p>

          <form onSubmit={(e) => handleSubmit.mutate(e)}>
            {/* AUTOCOMPLETE USERNAME/NAMA */}
            <div className="form-group mb-3" ref={wrapperRef} style={{ position: "relative" }}>
              <label className="login-label">Username / Nama Siswa</label>
              <input
                type="text"
                className="form-control login-input"
                value={usernameQuery}
                onChange={(e) => {
                  setUsernameQuery(e.target.value);
                  setIsSuggestOpen(true);
                }}
                placeholder="Ketik beberapa huruf nama siswa..."
                required
                autoFocus
                autoComplete="off"
              />

              {/* Dropdown */}
              {isSuggestOpen && (suggestLoading || suggestList.length > 0 || suggestError) && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 999,
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 12,
                    marginTop: 8,
                    boxShadow: "0 14px 40px rgba(0,0,0,0.10)",
                    overflow: "hidden",
                  }}
                >
                  {suggestLoading ? (
                    <div style={{ padding: 12, fontSize: 13, opacity: 0.8 }}>
                      Memuat data...
                    </div>
                  ) : suggestError ? (
                    <div style={{ padding: 12, fontSize: 13, color: "#b42318" }}>
                      {suggestError}
                    </div>
                  ) : (
                    <div style={{ maxHeight: 280, overflowY: "auto" }}>
                      {suggestList.map((item, idx) => (
                        <button
                          key={`${item?.nisn || item?.id || idx}`}
                          type="button"
                          onClick={() => onPickStudent(item)}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "10px 12px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            borderBottom:
                              idx === suggestList.length - 1
                                ? "none"
                                : "1px solid rgba(0,0,0,0.06)",
                          }}
                          onMouseDown={(e) => e.preventDefault()} // supaya input tidak blur duluan
                        >
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                            {item?.nama_lengkap || item?.nama_siswa || "-"} - {item?.ysb_school_id}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                            {buildStudentLabel(item)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* NISN tersembunyi (payload login), tetap bisa dipakai kalau user mau ketik manual via dev */}
              <input type="hidden" name="nisn" value={nisn} readOnly />
            </div>

            {/* PASSCODE */}
            {/* <div className="form-group mb-4">
              <label className="login-label">Passcode</label>
              <input
                type="password"
                className="form-control login-input"
                value={ysb_passcode_student}
                onChange={handleChange}
                name="ysb_passcode_student"
                required
              />
            </div> */}

            {/* PASSCODE */}
<div className="form-group mb-4">
  <label className="login-label">Passcode</label>

  <div style={{ position: "relative" }}>
    <input
      type={showPasscode ? "text" : "password"}
      className="form-control login-input"
      value={ysb_passcode_student}
      onChange={handleChange}
      name="ysb_passcode_student"
      required
      style={{ paddingRight: "48px" }}
    />

    <button
      type="button"
      onClick={() => setShowPasscode((prev) => !prev)}
      aria-label={showPasscode ? "Sembunyikan passcode" : "Lihat passcode"}
      style={{
        position: "absolute",
        right: "14px",
        top: "50%",
        transform: "translateY(-50%)",
        border: "none",
        background: "transparent",
        color: "#6B7280",
        cursor: "pointer",
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showPasscode ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
    </button>
  </div>
</div>

            {loading ? (
              <Button
                className="w-100 login-btn"
                type="submit"
                color="warning"
                style={{
                  padding: "10px 0",
                  borderRadius: "10px",
                  color: "#fff",
                }}
              >
                <ScaleLoader color={"white"} height={20} width={4} />
              </Button>
            ) : (
              <Button
                className="w-100 login-btn"
                type="submit"
                color="primary"
                style={{
                  padding: "10px 0",
                  borderRadius: "10px",
                  backgroundColor: "#4F46E5",
                  color: "#fff",
                  border: "none",
                }}
              >
                Login
              </Button>
            )}
          </form>
        </div>
      </div>

      <footer className="login-footer">
        Copyright 2026. Yayasan Syiar Bangsa. All rights reserved.
      </footer>
    </div>
  );
}

const styles = {
  logo: {
    width: "64px",
    height: "64px",
    borderRadius: "16px",
    margin: "0 auto 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "30px",
    background: "#EEF2FF",
    boxShadow: "0 8px 20px rgba(79,70,229,0.18)",
  },
};